import React, { useState } from "react";
import { Field } from "redux-form";
import PropTypes from "prop-types";
import map from "lodash/map";
import get from "lodash/get";
import slice from "lodash/slice";
import isEmpty from "lodash/isEmpty";
import FieldError from "components/FormFields/FieldError";
import Label from "components/FormFields/Label";
import FormattedMessage from "components/FormattedMessage";
import { Flex, Box, Button, Label as LabelUI } from "ui-components";
import {
  ChevronDown,
  ChevronUp,
  PlusCircleOutline,
  MinusCircleOutline
} from "components/Icons";
import { COLORS } from "ui-components/theme";
import Spinner, { styles } from "components/Spinner";
import DateSlotSelectButton from "components/DateSlotSelectButton";

const renderField = ({
  items,
  minItems,
  maxItems,
  title,
  choose_another_text,
  see_more_text,
  see_less_text,
  themeColor,
  tooltip,
  required,
  label,
  meta: { asyncValidating, touched, error },
  showDates,
  setShowDates,
  moreDateCount,
  setMoreDateCount,
  buttonTheme,
  primaryColor,
  handleSelect,
  fieldKeyFullData,
  formValues
}) => {
  return (
    <>
      {label && (
        <Label
          label={<FormattedMessage id={label} />}
          tooltip={tooltip}
          required={required}
        />
      )}
      <Box mb={6}>
        <FieldError touched={touched} error={error} />
      </Box>

      <Box
        mb={6}
        variant="borderSection"
        sx={{
          color: themeColor,
          borderColor: themeColor
        }}
      >
        {title && (
          <LabelUI
            sx={{
              color: "inherit",
              fontSize: [
                "1rem !important",
                "1.1rem !important",
                "1.2rem !important"
              ],
              cursor: "pointer"
            }}
            mb={showDates ? 4 : 0}
            onClick={() => setShowDates(!showDates)}
          >
            <Flex
              justifyContent="space-between"
              alignItems="center"
              width="100%"
            >
              <FormattedMessage html id={title} />
              <Button variant="closeButton" as="a">
                {showDates ? (
                  <ChevronUp size={"30px"} color={themeColor} />
                ) : (
                  <ChevronDown size={"30px"} color={themeColor} />
                )}
              </Button>
            </Flex>
          </LabelUI>
        )}
        {showDates && isEmpty(items) && <Spinner sx={styles.modalContainer} />}
        {showDates && !isEmpty(items) && (
          <>
            <Flex flexWrap="wrap" mx={-1} mb={3}>
              {map(slice(items, 0, moreDateCount), item => {
                let isSelected =
                  get(formValues, `${fieldKeyFullData}.id`) === get(item, "id")
                    ? true
                    : false;

                return (
                  <DateSlotSelectButton
                    key={item.id}
                    onSelect={handleSelect}
                    buttonTheme={buttonTheme}
                    selected={isSelected}
                    {...item}
                  />
                );
              })}
            </Flex>

            <Flex
              data-testid="number-select-toggle"
              textAlign="center"
              alignItems="center"
              justifyContent="center"
              onClick={() => {
                const numberCount =
                  moreDateCount < minItems || moreDateCount === maxItems
                    ? minItems
                    : maxItems;
                setMoreDateCount(numberCount);
              }}
              color="inherit"
              fontWeight="500"
              fontSize={["14px", "16px"]}
              sx={{
                cursor: "pointer",
                "& > svg": { margin: 0 },
                color: `${primaryColor} !important`
              }}
            >
              <FormattedMessage
                id={
                  moreDateCount === 0
                    ? choose_another_text
                    : items.length > moreDateCount
                    ? see_more_text
                    : see_less_text
                }
              />
              &nbsp;
              {items.length > moreDateCount ? (
                <PlusCircleOutline color={primaryColor} />
              ) : (
                <MinusCircleOutline color={primaryColor} />
              )}
            </Flex>
          </>
        )}
      </Box>
    </>
  );
};

const DateSlotSelection = ({
  items,
  initialItems,
  collapsed,
  price,
  see_more_text,
  see_less_text,
  theme,
  themeColors,
  tooltip,
  required,
  label,
  formValues,
  field,
  updateFormValues,
  fieldKeyFullData,
  minItems,
  maxItems,
  title
}) => {
  const [moreDateCount, setMoreDateCount] = useState(initialItems);
  const [showDates, setShowDates] = useState(!collapsed);

  const primaryColor = get(themeColors, "primary") || COLORS.blue[4];

  const handleSelect = e => {
    let selectedDate = e.currentTarget.dataset.dateId;
    let selected = items.find(dateItem => dateItem.id === selectedDate);

    //dispatch
    updateFormValues(selected);
  };

  return (
    <Box>
      <Field
        name={field}
        required={required}
        tooltip={tooltip}
        component={renderField}
        formValues={formValues}
        showDates={showDates}
        setShowDates={setShowDates}
        moreDateCount={moreDateCount}
        setMoreDateCount={setMoreDateCount}
        see_more_text={see_more_text}
        see_less_text={see_less_text}
        handleSelect={handleSelect}
        primaryColor={primaryColor}
        items={items}
        selected={get(formValues, "scheduled_date_full_data", {})}
        label={label}
        fieldKeyFullData={fieldKeyFullData}
        minItems={minItems}
        maxItems={maxItems}
        title={title}
      />
    </Box>
  );
};

DateSlotSelection.defaultProps = {
  initialItems: 0,
  minItems: 6,
  maxItems: 30,
  items: [],
  price: {},
  title: "",
  choose_another_text: "choose_another_date",
  timeTitle: "date_slot_selection_time_title",
  timeDisplay: "date_slot_selection_time_display",
  see_more_text: "see_more_dates",
  see_less_text: "see_less_dates",
  collapsed: true,
  field: "scheduled_date",
  formValues: {},
  required: true
};

DateSlotSelection.propTypes = {
  items: PropTypes.array.isRequired
};

export default DateSlotSelection;
